import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { MobileAlertDialog } from './MobileAlertDialog'

/**
 * <ConfirmProvider> — the project's imperative confirm dialog, the async
 * replacement for `window.confirm()` (which blocks the main thread on mobile).
 *
 * Mount once near the app root (src/main.tsx) — like <ToastProvider>. It renders
 * a single <MobileAlertDialog> and exposes an async `confirm()` via `useConfirm()`:
 *
 *   const confirm = useConfirm()
 *   if (!(await confirm({ title: 'Discard changes?', confirmVariant: 'danger' }))) return
 *   doTheThing()
 *
 * `confirm()` resolves `true` on Confirm, `false` on Cancel / Escape / back /
 * scrim-attempt (any non-confirm path). Only one confirm is shown at a time; a
 * `confirm()` called while another is pending supersedes it (the pending one
 * resolves `false`). Backs <MobileAlertDialog>, which is built on
 * `@radix-ui/react-dialog` (`modal={isDesktop}`, `role="alertdialog"`, outside-
 * click blocked) — see shadcn-adoption-plan Phase 7.
 *
 * Note: `window.confirm` is synchronous; this is async, so every call site must
 * become `if (!(await confirm({...}))) return` (the close/navigation it guards
 * is deferred until the user chooses). See Track E in overlay-migration-backlog.
 */

export interface ConfirmOptions {
  /** Bold title (required by Radix for a11y). Keep it short; put detail in `description`. */
  title: React.ReactNode
  /** Supporting message under the title. */
  description?: React.ReactNode
  /** Extra body content below the description. */
  children?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Confirm button style. `'danger'` for destructive confirms. Defaults to `'primary'`. */
  confirmVariant?: 'primary' | 'danger'
}

interface Pending {
  options: ConfirmOptions
  resolve: (ok: boolean) => void
}

const ConfirmContext = React.createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null)

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const [pending, setPending] = React.useState<Pending | null>(null)
  // The current resolver, held in a ref so Confirm-then-Cancel (MobileAlertDialog calls
  // onConfirm then onClose) settles exactly once: the first settle nulls it, the second is a no-op.
  const resolverRef = React.useRef<((ok: boolean) => void) | null>(null)

  const confirm = React.useCallback((options: ConfirmOptions) => {
    // Supersede any in-flight confirm (resolves it false) so we never show two dialogs.
    resolverRef.current?.(false)
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
      setPending({ options, resolve })
    })
  }, [])

  const settle = React.useCallback((ok: boolean) => {
    const resolve = resolverRef.current
    resolverRef.current = null
    setPending(null)
    resolve?.(ok)
  }, [])

  // If the provider unmounts mid-confirm, resolve false so the awaiting caller doesn't hang.
  React.useEffect(
    () => () => {
      resolverRef.current?.(false)
      resolverRef.current = null
    },
    [],
  )

  const options = pending?.options

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <MobileAlertDialog
        open={pending !== null}
        onClose={() => settle(false)}
        onConfirm={() => settle(true)}
        title={options?.title ?? ''}
        description={options?.description}
        // The provider owns the (FR) button labels so callers only pass title/description.
        confirmLabel={options?.confirmLabel ?? t('common.confirm', 'Confirmer')}
        cancelLabel={options?.cancelLabel ?? t('common.cancel', 'Annuler')}
        confirmVariant={options?.confirmVariant}
      >
        {options?.children}
      </MobileAlertDialog>
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = React.useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm must be used within a <ConfirmProvider> (mount it near the app root).')
  }
  return ctx
}

export default ConfirmProvider
